import React, { useState, useEffect } from 'react';
import api from '../../../utils/api';
import CommentsSection from './CommentsSection';

const CustomizedBookView = ({ user, selectedIdx, setSelectedIdx, onChaptersLoaded, comments, newComment, setNewComment, isLoadingComments, postingComment, commentError, handleCommentSubmit }) => {
  const [customizedChapters, setCustomizedChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [autoStartGeneration, setAutoStartGeneration] = useState(true);

  useEffect(() => {
    const loadCustomizedChapters = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('app_token');
        if (!token) {
          throw new Error('No authentication token');
        }

        // Fetch customized book content from user_customized_books table
        const response = await api.get('/api/books/customized-book/chapters', {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          },
          params: { _t: Date.now() }
        });

        console.log('🎯 Customized book API response:', response.data);
        
        if (response.data.status === 'completed' && response.data.chapters && response.data.chapters.length > 0) {
          // Use the chapters array from the API response (already in correct order)
          console.log('📚 Using chapters from API response:', response.data.chapters.length);
          console.log('📚 Chapters:', response.data.chapters);
          
          setCustomizedChapters(response.data.chapters);
          if (onChaptersLoaded) onChaptersLoaded(response.data.chapters);
          setLoading(false);
        } else if (response.data.status === 'completed' && response.data.customized_content) {
          // Fallback: Parse the HTML content to extract chapters
          const htmlContent = response.data.customized_content;
          console.log('📄 HTML Content length:', htmlContent.length);
          console.log('📄 HTML Content preview:', htmlContent.substring(0, 500));
          
          const chapters = parseChaptersFromHTML(htmlContent);
          
          console.log('Parsed chapters from HTML:', chapters);
          setCustomizedChapters(chapters);
          if (onChaptersLoaded) onChaptersLoaded(chapters);
          setLoading(false);
        } else if (response.data.status === 'processing') {
          // Book is being generated, start polling for progress
          console.log('🔄 Book is being generated, starting progress polling');
          setLoading(false);
          setIsGenerating(true);
          startProgressPolling();
        } else if (response.data.status === 'not_found' && autoStartGeneration) {
          // No customized book found, automatically start generation
          console.log('🚀 No customized book found, automatically starting generation');
          setLoading(false);
          await handleGenerateBook();
        } else {
          console.log('❌ No customized content found. Status:', response.data.status);
          setLoading(false);
          // Don't throw error, just show the generation button
        }
      } catch (err) {
        console.error('Error loading customized book:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadCustomizedChapters();
  }, [autoStartGeneration]);

  // Function to start progress polling
  const startProgressPolling = () => {
    const pollInterval = setInterval(async () => {
      try {
        const token = localStorage.getItem('app_token');
        const response = await api.get('/api/customized-book/progress', {
          headers: { Authorization: `Bearer ${token}` }
        });

        const { progress, status, totalChapters } = response.data;
        setGenerationProgress(progress || 0);

        if (status === 'completed') {
          clearInterval(pollInterval);
          setIsGenerating(false);
          // Reload the customized book
          window.location.reload();
        } else if (status === 'failed') {
          clearInterval(pollInterval);
          setIsGenerating(false);
          setError('Book generation failed. Please try again.');
        }
      } catch (err) {
        console.error('Error polling progress:', err);
        clearInterval(pollInterval);
        setIsGenerating(false);
        setError('Error checking generation progress');
      }
    }, 2000); // Poll every 2 seconds

    // Cleanup interval on unmount
    return () => clearInterval(pollInterval);
  };

  // Function to generate customized book
  const handleGenerateBook = async () => {
    try {
      setIsGenerating(true);
      setError(null);

      const token = localStorage.getItem('app_token');
      const response = await api.post('/api/customized-book/start', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('🚀 Book generation started:', response.data);

      if (response.data.status === 'processing') {
        // Start polling for progress
        startProgressPolling();
      } else if (response.data.status === 'completed') {
        // Book already exists, reload the page
        window.location.reload();
      }
    } catch (err) {
      console.error('Error starting book generation:', err);
      const errorMessage = err.response?.data?.error || 'Failed to start book generation';
      setError(errorMessage);
      setIsGenerating(false);
    }
  };

  // Function to parse chapters from HTML content
  const parseChaptersFromHTML = (htmlContent) => {
    const chapters = [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    
    // Find all chapter divs
    const chapterDivs = doc.querySelectorAll('.chapter');
    
    console.log('🔍 Found chapter divs:', chapterDivs.length);
    
    chapterDivs.forEach((chapterDiv, index) => {
      const titleElement = chapterDiv.querySelector('h2, h3');
      
      if (titleElement) {
        const title = titleElement.textContent.trim();
        
        // Clone the chapter div and remove the title element to get clean content
        const chapterClone = chapterDiv.cloneNode(true);
        const titleClone = chapterClone.querySelector('h2, h3');
        if (titleClone) {
          titleClone.remove();
        }
        
        // Get the remaining content
        const content = chapterClone.innerHTML.trim();
        
        console.log(`📖 Chapter ${index + 1}:`, { title, contentLength: content.length });
        
        chapters.push({
          id: `chapter-${index}`,
          title: title,
          content: content,
          image_url: null,
          section_id: null,
          order: index + 1
        });
      }
    });
    
    console.log('📚 Total chapters parsed:', chapters.length);
    return chapters;
  };

  const handleChapterSelect = (index) => {
    setSelectedIdx(index);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-title">Loading your personalized book...</div>
        <div className="loading-subtitle">This may take a few moments</div>
      </div>
    );
  }

  if (error) {
    const isCustomizationError = error.includes('customization settings');
    
    return (
      <div className="error-container">
        <div className="error-title">
          {isCustomizationError ? 'Customization Required' : 'Error loading customized book'}
        </div>
        <div className="error-message">{error}</div>
        {isCustomizationError ? (
          <div style={{ marginTop: '20px' }}>
            <button 
              onClick={() => window.location.href = '/settings'}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginRight: '10px'
              }}
            >
              Complete Customization Settings
            </button>
            <button 
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        ) : (
          <button 
            onClick={() => window.location.reload()}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  if (!customizedChapters || customizedChapters.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading-title">No customized book available</div>
        <div className="loading-subtitle">
          {isGenerating 
            ? `Generating your personalized book... ${generationProgress}% complete`
            : 'Click the button below to generate your personalized book based on your preferences.'
          }
        </div>
        {!isGenerating && (
          <button 
            onClick={handleGenerateBook}
            disabled={isGenerating}
            style={{
              marginTop: '20px',
              padding: '12px 24px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              opacity: isGenerating ? 0.6 : 1
            }}
          >
            {isGenerating ? 'Generating...' : 'Generate My Personalized Book'}
          </button>
        )}
        {isGenerating && (
          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <div style={{ 
              width: '100%', 
              height: '8px', 
              backgroundColor: '#e9ecef', 
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${generationProgress}%`,
                height: '100%',
                backgroundColor: '#28a745',
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
              {generationProgress}% complete
            </div>
          </div>
        )}
      </div>
    );
  }

  const currentChapter = customizedChapters[selectedIdx];
  const isFirstChapter = selectedIdx === 0;
  const isLastChapter = selectedIdx === customizedChapters.length - 1;

  const handlePrevious = () => {
    if (!isFirstChapter) {
      setSelectedIdx(selectedIdx - 1);
    }
  };

  const handleNext = () => {
    if (!isLastChapter) {
      setSelectedIdx(selectedIdx + 1);
    }
  };

  return (
    <div className="book-content-container">
      {/* Chapter Content */}
      {currentChapter && (
        <>
          {/* Chapter Banner Image or Title */}
          {currentChapter.image_url ? (
            <div 
              className="chapter-header-image"
              style={{
                backgroundImage: `url(${currentChapter.image_url})`
              }}
            >
              <div className="chapter-header-overlay" />
              <h2 className="book-chapter-title chapter-title">
                {currentChapter.title}
              </h2>
            </div>
          ) : (
            <h2 className="book-chapter-title" style={{ textAlign: 'center', margin: '32px 0 24px 0' }}>
              {currentChapter.title}
            </h2>
          )}
          
          <div
            className="book-preview-chapter-content"
            dangerouslySetInnerHTML={{ __html: currentChapter.content || '<em>No content</em>' }}
          />
          
          {/* Navigation Buttons */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '32px', padding: '0 32px' }}>
            <button
              onClick={handlePrevious}
              disabled={isFirstChapter}
              className="btn-secondary"
              style={{
                opacity: isFirstChapter ? 0.5 : 1,
                cursor: isFirstChapter ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <span className="material-icons">arrow_back</span>
              Previous
            </button>
            
            <span style={{ color: '#666', fontSize: '14px' }}>
              Chapter {selectedIdx + 1} of {customizedChapters.length}
            </span>
            
            <button
              onClick={handleNext}
              disabled={isLastChapter}
              className="btn-primary"
              style={{
                opacity: isLastChapter ? 0.5 : 1,
                cursor: isLastChapter ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              Next
              <span className="material-icons">arrow_forward</span>
            </button>
          </div>
          
          {/* Comments Section */}
          <CommentsSection
            comments={comments}
            newComment={newComment}
            setNewComment={setNewComment}
            isLoadingComments={isLoadingComments}
            postingComment={postingComment}
            commentError={commentError}
            handleCommentSubmit={handleCommentSubmit}
            user={user}
          />
        </>
      )}
    </div>
  );
};

export default CustomizedBookView; 